'use client';
import React, { useState } from 'react';
import { Button, Input, Select } from '@ui';
import DataTableControls from './DataTableControls';
import DataTableStructure from './DataTableStructure';
import {
  DEFAULT_TH_TITLES,
  DEFAULT_PHASE_PLACEHOLDER,
  DEFAULT_PHASE_SORT_KEY,
  DEFAULT_STATUS_PLACEHOLDER,
  DEFAULT_STATUS_SORT_KEY,
  sampleData,
  type ColumnDef,
  type SortKey,
} from './DataTableData';

export type { ColumnDef } from './DataTableData';

const DEFAULT_TABLE_HINT_WITH_CONTROLS =
  'Click headers to sort. Rows per page selectable below.';

type DataTableProps = {
  tableId: string;
  tableHelpId: string;
  thTitles?: ColumnDef[];
  phaseSortKey?: SortKey;
  statusSortKey?: SortKey;
  phasePlaceholder?: string;
  statusPlaceholder?: string;
  tableClassName?: string;
  /**
   * When true (default), `mobile-max` uses `.table-sort-mobile` + `.table-wrap-mobile` + table `table-mobile` (see `_tables.scss`).
   * Set false for a classic table at all breakpoints.
   */
  mobileTableLayout?: boolean;
  /**
   * When true (default), renders `.table-controls` (pagination + page size). When false, that block is omitted; sort/filter/table behavior is unchanged (internal paging still applies to visible rows).
   */
  showControls?: boolean;
  /** Primary hint when `showControls` is true. Defaults to copy that mentions sorting and rows per page. */
  tableHintWithControlsText?: string;
  /** With `showControls`: optional second hint; both hints render as sibling `<span>`s inside an outer `<span>`. Without `showControls`: sole hint text (non-empty to show the `<small>`). */
  tableHintText?: string;
  /** Appended to `className` on the hint `<small>` (after `hint`). */
  tableHintClasses?: string;
};

const DataTable: React.FC<DataTableProps> = ({
  tableId,
  tableHelpId,
  thTitles,
  phaseSortKey = DEFAULT_PHASE_SORT_KEY,
  statusSortKey = DEFAULT_STATUS_SORT_KEY,
  phasePlaceholder = DEFAULT_PHASE_PLACEHOLDER,
  statusPlaceholder = DEFAULT_STATUS_PLACEHOLDER,
  tableClassName,
  mobileTableLayout = true,
  showControls = true,
  tableHintWithControlsText = DEFAULT_TABLE_HINT_WITH_CONTROLS,
  tableHintText,
  tableHintClasses,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [phaseQuery, setPhaseQuery] = useState('');
  const [statusQuery, setStatusQuery] = useState('');
  const normalizedPhaseQuery = phaseQuery.trim().toLowerCase();
  const normalizedStatusQuery = statusQuery.trim().toLowerCase();
  const filteredData = sampleData.filter((row) => {
    const phaseValue = String(row[phaseSortKey] ?? '').toLowerCase();
    const statusValue = String(row[statusSortKey] ?? '').toLowerCase();
    const phaseMatch = normalizedPhaseQuery ? phaseValue.includes(normalizedPhaseQuery) : true;
    const statusMatch = normalizedStatusQuery ? statusValue.includes(normalizedStatusQuery) : true;
    return phaseMatch && statusMatch;
  });
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleFirst = () => setPage(1);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const handleLast = () => setPage(totalPages);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key as SortKey);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleMobileSortColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.value as SortKey;
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir('asc');
      setPage(1);
    }
  };

  const handleMobileSortDirToggle = () => {
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const handlePhaseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setPhaseQuery(nextValue);
    if (sortKey !== phaseSortKey) {
      setSortKey(phaseSortKey);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleStatusInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setStatusQuery(nextValue);
    if (sortKey !== statusSortKey) {
      setSortKey(statusSortKey);
      setSortDir('asc');
    }
    setPage(1);
  };

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    const v1: string | number = a[sortKey];
    const v2: string | number = b[sortKey];
    if (typeof v1 === 'number' && typeof v2 === 'number') {
      return sortDir === 'asc' ? v1 - v2 : v2 - v1;
    }
    // string comparison
    const s1 = String(v1).toLowerCase();
    const s2 = String(v2).toLowerCase();
    if (s1 < s2) return sortDir === 'asc' ? -1 : 1;
    if (s1 > s2) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const startIdx = (page - 1) * pageSize;
  const visibleRows = sortedData.slice(startIdx, startIdx + pageSize);

  // Compute columns from prop or defaults
  const columns: ColumnDef[] = thTitles && thTitles.length ? thTitles : DEFAULT_TH_TITLES;

  const wrapBase = mobileTableLayout !== false ? 'table-wrap table-wrap-mobile' : 'table-wrap';
  const wrapClass = showControls ? wrapBase : `${wrapBase} table-wrap-rounded`;

  const hasTableHintText = typeof tableHintText === 'string' && tableHintText.trim().length > 0;
  const showTableHint = showControls || hasTableHintText;
  const hintClassName = ['hint', tableHintClasses].filter(Boolean).join(' ');

  let tableHintContent: React.ReactNode = null;
  if (showControls && hasTableHintText) {
    tableHintContent = (
      <span>
        <span>{tableHintWithControlsText}</span>
        <span>{tableHintText.trim()}</span>
      </span>
    );
  } else if (showControls) {
    tableHintContent = tableHintWithControlsText;
  } else if (hasTableHintText) {
    tableHintContent = tableHintText.trim();
  }

  return (
    <div className='table-container'>
      {mobileTableLayout !== false ? (
        <div className='table-sort-mobile' aria-label='Table sort (mobile)'>
          <span id={`${tableId}-mobile-sort-label`} className='table-sort-label'>
            Sort by:
          </span>
          <Select
            id={`${tableId}-mobile-sort`}
            className='select select-sm'
            value={sortKey}
            onChange={handleMobileSortColumnChange}
            options={columns.map((c) => ({ value: c.key, label: c.title }))}
            aria-labelledby={`${tableId}-mobile-sort-label`}
          />
          <Button
            type='button'
            className='btn-outline table-sort-button'
            aria-label={
              sortDir === 'asc'
                ? 'Sort ascending, switch to descending'
                : 'Sort descending, switch to ascending'
            }
            onClick={handleMobileSortDirToggle}
            label={sortDir === 'asc' ? 'Asc ↑' : 'Desc ↓'}
          />
        </div>
      ) : null}
      <div className={wrapClass}>
        <DataTableStructure
          tableId={tableId}
          tableHelpId={tableHelpId}
          columns={columns}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          visibleRows={visibleRows}
          tableClassName={tableClassName}
          mobileTableLayout={mobileTableLayout}
          getFilterRowCell={(key) => {
            if (key === phaseSortKey) {
              return {
                className: 'table-subheader',
                children: (
                  <Input
                    placeholder={phasePlaceholder}
                    value={phaseQuery}
                    onChange={handlePhaseInputChange}
                  />
                ),
              };
            }
            if (key === statusSortKey) {
              return {
                children: (
                  <Input
                    placeholder={statusPlaceholder}
                    value={statusQuery}
                    onChange={handleStatusInputChange}
                  />
                ),
              };
            }
            return null;
          }}
        />
      </div>
      {showTableHint ? (
        <small id={tableHelpId} className={hintClassName}>
          {tableHintContent}
        </small>
      ) : null}

      {showControls ? (
        <DataTableControls
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onFirst={handleFirst}
          onPrev={handlePrev}
          onNext={handleNext}
          onLast={handleLast}
        />
      ) : null}
    </div>
  );
};

export default DataTable;
